import { Component, Inject, OnInit, Renderer2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { getMessaging, onMessage } from "@angular/fire/messaging";
import { Store } from "@ngrx/store";

import { AuthService, MessagingService } from "./services";
import {
  VCFO_ORGANIZATION_ID,
  QUPIL_ORGANIZATION_ID,
  ORGANIZATION_ID,
  QUPIL_LANGUAGE_EN,
  VCFO_LANGUAGE_EN,
} from "./constants";
import { NavigationError, Router } from "@angular/router";
import { loadNotifications } from "./shared-modules/navbar/store/actions";
import { IQuery } from "./shared-modules/navbar/models";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  private messaging;
  private readonly notificationRefreshQuery: IQuery = { page: 1, limit: 7 };
  constructor(
    private message: MessagingService,
    private _renderer2: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    private authService: AuthService,
    private router: Router,
    private store: Store
  ) {}

  async ngOnInit() {
    // Handle chunk load failures (e.g. lazy-loaded Siraj module) — reload once to fetch fresh chunks.
    // Uses sessionStorage gate to prevent infinite reload loops if the chunk is permanently unavailable.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError && event.error?.name === 'ChunkLoadError') {
        const key = 'chunk_load_failed';
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.assign(event.url);
        } else {
          sessionStorage.removeItem(key);
        }
      }
    });

   // Initialize messaging instance (with error handling for unsupported browsers/guests)
   try {
     this.messaging = getMessaging();
   } catch (error) {
     // Firebase messaging not available - skip messaging features
     // This is common on mobile browsers or guest routes
     this.messaging = null;
   }

    // Organization and language setup logic
    const baseUrl = window.location.origin;
    if (
      baseUrl.includes("vcfo") &&
      this.authService.getOrganizationId() !== VCFO_ORGANIZATION_ID
    ) {
      this.authService.setOrganizationId(VCFO_ORGANIZATION_ID);
      if (!localStorage.getItem("en")) this.authService.setLanguage("en", VCFO_LANGUAGE_EN);
      window.location.reload();
    } else if (
      !baseUrl.includes("vcfo") &&
      this.authService.getOrganizationId() !== QUPIL_ORGANIZATION_ID
    ) {
      this.authService.setOrganizationId(QUPIL_ORGANIZATION_ID);
      if (!localStorage.getItem("en")) this.authService.setLanguage("en", QUPIL_LANGUAGE_EN);
      window.location.reload();
    }

    // Request messaging permissions and subscribe to messaging service
    if (this.authService.getOrganizationId() == QUPIL_ORGANIZATION_ID) {
      // Request permission (errors are handled silently in the service)
      await this.message.requestPermission().catch(() => {
        // Silently handle - permission issues are user choices
      });
    }

    // Listen for incoming messages using the updated method (only if messaging is available)
    if (this.messaging) {
      onMessage(this.messaging, (payload) => {
        this.displayForegroundNotification(payload);
        this.refreshNavbarNotifications();
      });
    }

    // Get images, languages, and icons
    await this.authService.getLogoAndImages(ORGANIZATION_ID).toPromise();
    await this.authService.getLanguages(ORGANIZATION_ID).toPromise();

    var link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("type", "image/x-icon");
    link.setAttribute("href", localStorage.getItem("logo"));
    document.head.appendChild(link);
  }

  private displayForegroundNotification(payload: any) {
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    const title =
      payload?.notification?.title || payload?.data?.title || "Qupil";

    const notificationOptions: NotificationOptions = {
      body: payload?.notification?.body || payload?.data?.body || "",
      icon:
        payload?.notification?.icon ||
        payload?.data?.icon ||
        "/assets/icons/icon-192x192.png",
      data: {
        clickAction:
          payload?.data?.clickAction ||
          payload?.fcmOptions?.link ||
          window.location.origin,
        ...payload?.data,
      },
    };

    const notification = new Notification(title, notificationOptions);
    notification.onclick = () => {
      notification.close();
      const clickTarget =
        notificationOptions.data?.clickAction || window.location.origin;
      window.open(clickTarget, "_blank");
    };
  }

  private refreshNavbarNotifications() {
    this.store.dispatch(
      loadNotifications({
        payload: {
          organizationId: ORGANIZATION_ID,
          query: this.notificationRefreshQuery,
        },
      })
    );
  }
}
